import type { ThemeTokens } from "./types";

export interface ResolvedTheme {
  colorScheme: "light" | "dark";
  colors: {
    primary: string;
    primaryForeground: string;
    background: string;
    foreground: string;
    muted: string;
    mutedForeground: string;
    border: string;
  };
  radius: number;
  fontFamily?: string;
}

const LIGHT: ResolvedTheme["colors"] = {
  primary: "#6366f1",
  primaryForeground: "#ffffff",
  background: "#ffffff",
  foreground: "#0e1525",
  muted: "#f1f5f9",
  mutedForeground: "#5b6678",
  border: "#eef1f6",
};

const DARK: ResolvedTheme["colors"] = {
  // Keep the accent vivid with a white foreground in both modes — the FAB is a
  // saturated brand-colored circle, so its icon should always read white.
  primary: "#6366f1",
  primaryForeground: "#ffffff",
  background: "#14161d",
  foreground: "#f3f4f8",
  muted: "#232733",
  mutedForeground: "#9aa3b2",
  border: "#262a35",
};

// Merge precedence (lowest → highest): platform defaults, server config,
// developer theme prop. Returns concrete tokens the UI can render directly.
export function resolveTheme(
  server: ThemeTokens | null | undefined,
  local: ThemeTokens | null | undefined,
  systemColorScheme: "light" | "dark" = "light",
): ResolvedTheme {
  // Default to light: the host page's appearance is not the OS appearance, so
  // following the OS by default causes a light page + dark widget mismatch.
  // Developers opt into auto via colorScheme: "system".
  const requested = local?.colorScheme ?? server?.colorScheme ?? "light";
  const colorScheme: "light" | "dark" =
    requested === "system" ? systemColorScheme : requested;

  const base = colorScheme === "dark" ? DARK : LIGHT;
  const merged = {
    ...base,
    ...stripUndefined(server?.colors),
    ...stripUndefined(local?.colors),
  };

  return {
    colorScheme,
    colors: merged,
    radius: local?.radius ?? server?.radius ?? 12,
    fontFamily:
      (local?.typography?.fontFamily as string | undefined) ??
      (server?.typography?.fontFamily as string | undefined),
  };
}

function stripUndefined(
  obj: Record<string, string | undefined> | undefined,
): Record<string, string> {
  if (!obj) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) if (v != null) out[k] = v;
  return out;
}
